import type { GameEnvironment, Player } from "../game";
import type { GoState } from "./types";

const KOMI: Record<number, number> = { 3: 8.5, 4: 1.5, 5: 24.5, 6: 3.5, 7: 8.5, 8: 9.5, 9: 7.5, 10: 7.5, 13: 7.5, 19: 7.5 };

function neighbors(action: number, size: number): number[] {
  const row = Math.floor(action / size), column = action % size, out: number[] = [];
  if (row) out.push(action - size);
  if (row + 1 < size) out.push(action + size);
  if (column) out.push(action - 1);
  if (column + 1 < size) out.push(action + 1);
  return out;
}

function group(board: Int8Array, start: number, size: number): { stones: number[]; liberties: Set<number> } {
  const color = board[start], stones: number[] = [], liberties = new Set<number>(), seen = new Set([start]);
  const pending = [start];
  while (pending.length) {
    const action = pending.pop()!;
    stones.push(action);
    for (const adjacent of neighbors(action, size)) {
      if (board[adjacent] === -1) liberties.add(adjacent);
      else if (board[adjacent] === color && !seen.has(adjacent)) {
        seen.add(adjacent); pending.push(adjacent);
      }
    }
  }
  return { stones, liberties };
}

function playStone(board: Int8Array, action: number, player: Player, size: number) {
  const next = board.slice(); next[action] = player;
  let captured = 0, singleCapture = -1;
  for (const adjacent of neighbors(action, size)) {
    if (next[adjacent] !== 1 - player) continue;
    const enemy = group(next, adjacent, size);
    if (enemy.liberties.size === 0) {
      captured += enemy.stones.length;
      if (enemy.stones.length === 1) singleCapture = enemy.stones[0]!;
      for (const stone of enemy.stones) next[stone] = -1;
    }
  }
  const own = group(next, action, size);
  if (own.liberties.size === 0) return null;
  const ko = captured === 1 && own.stones.length === 1 && own.liberties.size === 1 ? singleCapture : -1;
  return { board: next, captured, ko };
}

function key(board: Int8Array): string { return Array.from(board, (x) => String(x + 1)).join(""); }

function score(board: Int8Array, size: number, komi: number): [number, number] {
  let black = 0, white = komi;
  const seen = new Set<number>();
  for (let i = 0; i < board.length; i += 1) {
    if (board[i] === 0) { black += 1; continue; }
    if (board[i] === 1) { white += 1; continue; }
    if (seen.has(i)) continue;
    const region: number[] = [], borders = new Set<number>(), pending = [i]; seen.add(i);
    while (pending.length) {
      const point = pending.pop()!; region.push(point);
      for (const adjacent of neighbors(point, size)) {
        if (board[adjacent] === -1 && !seen.has(adjacent)) { seen.add(adjacent); pending.push(adjacent); }
        else if (board[adjacent] !== -1) borders.add(board[adjacent]!);
      }
    }
    if (borders.size === 1) borders.has(0) ? black += region.length : white += region.length;
  }
  return [black, white];
}

export class PgxGo implements GameEnvironment<GoState> {
  readonly komi: number;
  constructor(readonly size: number) {
    if (!(size in KOMI)) throw new RangeError(`Unsupported Go size ${size}`);
    this.komi = KOMI[size]!;
  }

  init(): GoState {
    const board = new Int8Array(this.size * this.size).fill(-1);
    return {
      size: this.size, komi: this.komi, currentPlayer: 0, board,
      boardHistory: [], positionHistory: [], stepCount: 0, consecutivePasses: 0,
      captures: [0, 0], moves: [], lastAction: -1, ko: -1, terminated: false,
      terminalReason: null, winner: null, rewards: [0, 0], scores: [0, this.komi],
    };
  }

  step(state: GoState, action: number): GoState {
    const pass = this.size * this.size;
    const legal = this.legalActionMask(state);
    if (state.terminated || !Number.isInteger(action) || action < 0 || action > pass || !legal[action]) {
      const winner = (1 - state.currentPlayer) as Player;
      const rewards: [number, number] = [0, 0]; rewards[state.currentPlayer] = -1; rewards[winner] = 1;
      return { ...state, terminated: true, terminalReason: "illegal-action", winner, rewards };
    }
    let board = state.board, ko = -1, consecutivePasses = state.consecutivePasses + 1;
    const captures: [number, number] = [...state.captures];
    if (action < pass) {
      const played = playStone(state.board, action, state.currentPlayer, this.size)!;
      board = played.board; ko = played.ko; consecutivePasses = 0;
      captures[state.currentPlayer] += played.captured;
    }
    const boardKey = key(board);
    const repeat = action < pass && state.positionHistory.includes(boardKey);
    const stepCount = state.stepCount + 1;
    const maxed = stepCount >= 2 * this.size * this.size;
    const endedByPass = consecutivePasses >= 2;
    const terminated = repeat || maxed || endedByPass;
    const scores = score(board, this.size, this.komi);
    let winner: Player | null = null;
    if (repeat) winner = (1 - state.currentPlayer) as Player;
    else if (terminated) winner = scores[0] > scores[1] ? 0 : 1;
    const rewards: [number, number] = [0, 0];
    if (winner !== null) { rewards[winner] = 1; rewards[(1 - winner) as Player] = -1; }
    return {
      ...state, board, currentPlayer: (1 - state.currentPlayer) as Player, ko,
      stepCount, consecutivePasses, captures,
      boardHistory: [board.slice(), ...state.boardHistory].slice(0, 8),
      positionHistory: [...state.positionHistory, boardKey],
      moves: [...state.moves, { action, label: this.formatAction(state, action) }],
      lastAction: action, terminated, terminalReason: repeat ? "repeat" : endedByPass ? "two-pass" : maxed ? "max-steps" : null,
      winner, rewards, scores,
    };
  }

  observe(state: GoState): Float32Array {
    const output = new Float32Array(this.size * this.size * 17);
    for (let point = 0; point < state.board.length; point += 1) {
      for (let h = 0; h < 8; h += 1) {
        const stone = state.boardHistory[h]?.[point] ?? -1;
        output[point * 17 + h * 2] = stone === state.currentPlayer ? 1 : 0;
        output[point * 17 + h * 2 + 1] = stone === 1 - state.currentPlayer ? 1 : 0;
      }
      output[point * 17 + 16] = state.currentPlayer;
    }
    return output;
  }

  legalActionMask(state: GoState): Uint8Array {
    const count = this.size * this.size, mask = new Uint8Array(count + 1);
    if (state.terminated) return mask.fill(1);
    for (let action = 0; action < count; action += 1) {
      if (state.board[action] !== -1 || action === state.ko) continue;
      const played = playStone(state.board, action, state.currentPlayer, this.size);
      // PGX masks the immediate ko recapture. Longer positional repeats remain
      // selectable and terminate as a loss for the player who repeats.
      if (played) mask[action] = 1;
    }
    mask[count] = 1;
    return mask;
  }

  currentPlayer(state: GoState): Player { return state.currentPlayer; }
  isTerminal(state: GoState): boolean { return state.terminated; }
  terminalValue(state: GoState): number { return state.rewards[state.currentPlayer] ?? 0; }
  formatAction(_state: GoState, action: number): string {
    if (action === this.size * this.size) return "Pass";
    const column = action % this.size;
    return `${String.fromCharCode(65 + column + (column >= 8 ? 1 : 0))}${this.size - Math.floor(action / this.size)}`;
  }
}
